import { ArrowUturnLeftIcon, UserCircleIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { useImpersonation } from '@/features/impersonation/use-impersonation';
import { formatName } from '@/lib/format-name';
import { cn } from '@/lib/utils';

/**
 * MEGSZEMÉLYESÍTÉS-SÁV — a legfontosabb UX-elem a funkcióban: MINDIG látszik, hogy épp nem a saját nevünkben
 * dolgozunk, és EGY kattintással vissza lehet lépni.
 *
 * MIÉRT KÖTELEZŐ (nem díszítés): a megszemélyesítés a felület minden adatát kicseréli. Sáv nélkül a támogató
 * elfelejtheti, hogy más nevében ír egy jegyre vagy szerkeszt egy adatot — az iparági gyakorlat (és minden
 * audit-elvárás) ezért kimondja, hogy az állapot legyen folyamatosan látható. Ezért a sáv:
 *   • MINDKÉT keretben ott van (belső shell ÉS partner-portál — partner-célnál a portál töltődik be),
 *   • a TOPBAR ALATT, a görgetésen KÍVÜL (a keret fix eleme) — user-döntés: ez az iparági minta
 *     (Salesforce „logged in as", django-hijack, Nova/Filament), mert az F-alakú olvasás miatt a felső sáv
 *     a művelet ELŐTT jut a tudatba. Az első kiadás alul volt, hogy ne mozdítsa a lap-fejet; a mostani
 *     megoldás ezt a geometria-félelmet is kezeli: NEM teljes viewport-szélességű csík, hanem a
 *     LAP-TARTALOM szélességére igazított kártya (a kereteknél `mx-3`/`mx-4` — ugyanaz az igazítási vonal,
 *     amin a topbar-cím, az eszköztár és a lap-tartalom áll), és a `main` FÖLÖTT él a flex-kolumnában,
 *     tehát a `h-full` lista-lapok (rögzített `DataTable`-kártya) sem csúsznak ki,
 * • DANGER (piros/rózsa) tónusú — user-döntés: a sárga „figyelmeztetés" a felületen a lejáró
 *     kártyák és a válaszra váró jegyek jelzése; a más nevében dolgozás ennél EGY OSZTÁLLYAL feljebb van,
 *     és a piros az egyetlen tónus, amit nem lehet hétköznapi állapotnak olvasni.
 *
 * Az állapot a `/session`-ből jön (a token `act` claimje), tehát a lejárt megszemélyesítés után a sáv magától
 * eltűnik — nem egy kliens-flag „ragad" benne.
 */
export function ImpersonationBanner({ className }: { className?: string }): JSX.Element | null {
  const { t, i18n } = useTranslation();
  const {
    active,
    subjectName,
    subjectGivenName,
    subjectFamilyName,
    subjectEmail,
    actorName,
    actorGivenName,
    actorFamilyName,
    isPending,
    stop,
  } = useImpersonation();

  if (!active) return null;

  // LOCALE-TUDATOS NÉVSOR (hu → „Vezetéknév Keresztnév"), mint a listákon: ugyanaz a személy NE jelenjen meg
  // két néven. A sor-akció a `formatName`-mel írja a gomb feliratát, ezért a sáv is azt használja — enélkül
  // a felhasználó a listán „Abbázia Sales"-re kattint, és a sávban „Sales Abbázia" fogadja (mérve az e2e-n).
  const formatted = formatName(
    { givenName: subjectGivenName, familyName: subjectFamilyName, displayName: subjectName },
    i18n.language,
  );
  // A `formatName` név hiányában „—"-t ad (a lista-cellák üres jelölője) — a SÁVBAN ez értelmetlen lenne,
  // ezért ott az e-mail, végül a semleges megnevezés a fallback.
  const subject =
    formatted === '—' ? (subjectEmail ?? t('impersonation.unknownSubject')) : formatted;

  // AZ AKTOR NEVE UGYANAZZAL A SZABÁLLYAL. A token `act.name`-je a `display_name`, ami a migrált adatban
  // „keresztnév vezetéknév" — magyar felületen tehát rossz sorrend („Szilárd Reizner"), miközben ugyanaz a
  // kolléga a listákon „Reizner Szilárd". Ezért a szerver a NÉV-RÉSZEKET is adja, és a sorrendet itt döntjük.
  // Ha a részek hiányoznak (címtár nem elérhető), a `formatName` a `displayName`-re esik vissza — a sáv
  // sosem üres, csak a sorrend nem ideális.
  const actorFormatted = formatName(
    { givenName: actorGivenName, familyName: actorFamilyName, displayName: actorName },
    i18n.language,
  );
  const actor = actorFormatted === '—' ? null : actorFormatted;

  return (
    <div
      role="status"
      className={cn(
        // KÁRTYA-alak (nem edge-to-edge csík): `rounded-xl` + KÖRBE-szegély, mint a `DangerZone` doboza —
        // így a sáv a lap-tartalom szélességén ül, nem vágja át a felületet. A `--danger-soft` token TÖMÖR
        // (nem alfás), tehát a kártya alatt semmi nem üt át.
        'bg-danger-soft text-foreground border-danger-500/30 flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1.5 rounded-xl border px-3 py-2 text-[12.5px]',
        className,
      )}
    >
      <UserCircleIcon className="text-danger-600 dark:text-danger-400 size-[18px] shrink-0" />
      <span className="min-w-0 flex-1">
        <strong className="font-semibold">
          {t('impersonation.bannerTitle', { name: subject })}
        </strong>{' '}
        <span className="text-muted-foreground">
          {actor === null
            ? t('impersonation.bannerHintNoActor')
            : t('impersonation.bannerHint', { actor })}
        </span>
      </span>
      <Button
        variant="outline"
        size="sm"
        disabled={isPending}
        onClick={() => void stop()}
        className="shrink-0"
      >
        <ArrowUturnLeftIcon />
        {t('impersonation.stop')}
      </Button>
    </div>
  );
}
